import React from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import type { ClientRosterStatusFilter } from '@/types/client';

interface ClientStatusTabsProps {
  clientStatus: ClientRosterStatusFilter;
  onStatusChange: (status: ClientRosterStatusFilter) => void;
  statusTotals: {
    all?: number;
    active: number;
    suspended: number;
    drafts?: number;
    pendingDeletion?: number;
  };
}

const tabValues: ClientRosterStatusFilter[] = [
  'all',
  'active',
  'suspended',
  // Temporarily hidden on main.
  // 'draft',
  // 'pending_deletion',
];

const tabLabels: Record<ClientRosterStatusFilter, string> = {
  all: 'All Clients',
  active: 'Active Clients',
  suspended: 'Suspended',
  draft: 'Drafts',
  pending_deletion: 'Pending deletion',
};

export const ClientStatusTabs = ({ clientStatus, onStatusChange, statusTotals }: ClientStatusTabsProps) => {
  const { all = 0, active, suspended, drafts = 0, pendingDeletion = 0 } = statusTotals;
  const counts: Record<ClientRosterStatusFilter, number> = {
    all,
    active,
    suspended,
    draft: drafts,
    pending_deletion: pendingDeletion,
  };

  return (
    <Tabs
      value={clientStatus}
      onChange={(_, value) => onStatusChange(value as ClientStatusTabsProps["clientStatus"])}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      sx={{
        minHeight: 42,
        minWidth: 0,
        flexGrow: 1,
        flexShrink: 1,
        '& .MuiTabs-flexContainer': { gap: { xs: 0.5, sm: 1 } },
        '& .MuiTabs-indicator': {
          height: 2.5,
          borderRadius: '2px 2px 0 0',
          backgroundColor: '#25a8da',
        },
        '& .MuiTab-root': {
          minHeight: 42,
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          px: { xs: 1, sm: 1.25 },
          py: 0,
          minWidth: 'auto',
        },
      }}
    >
      {tabValues.map((value) => {
        const count = counts[value];
        const isActive = clientStatus === value;
        return (
          <Tab
            key={value}
            value={value}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? '#25a8da' : '#667085',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tabLabels[value]}
                </Typography>
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: '999px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    minWidth: 24,
                    textAlign: 'center',
                    bgcolor: isActive ? '#25a8da' : '#eaedf2',
                    color: isActive ? '#fff' : '#7f8a9a',
                  }}
                >
                  {count}
                </Box>
              </Box>
            }
          />
        );
      })}
    </Tabs>
  );
};
